import {
  UnifiedForecastResult,
  RebatePoint,
  RebateBreakdown,
} from '@erp/shared-types';
import type { MergedRebatePoint } from '../components/rebates/hooks/useSmartMergedForecastData';

// Tolerance for floating-point comparisons (handles rounding errors)
const EPSILON = 0.01; // Tolerance of one cent for currency values

// Helper to combine estimates and predictions into a unified points array for processing
// CRITICAL: Estimates take priority over predictions for the same date
// Adds isActual flag to distinguish between historical and predicted data
// Now determines isActual by comparing point dates against current UTC date
// (backend returns 0 for future months in estimates, so we can't rely on source)
export const getAllPoints = (
  forecastData: UnifiedForecastResult & {
    merged?: RebatePoint[] | MergedRebatePoint[];
  },
) => {
  const points: (RebatePoint & { isActual: boolean })[] = [];
  const estimateDates = new Set<string>();

  // Get current UTC date (start of today) for comparison
  const now = new Date();
  const currentUTCDate = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );

  // Helper to determine if a date is actual (past or today) vs projected (future)
  const isDateActual = (date: Date): boolean => {
    // Normalize to start of day for comparison
    const pointUTCDate = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
    );
    return pointUTCDate.getTime() <= currentUTCDate.getTime();
  };

  // If merged array exists (from smart merge), use it directly
  // This ensures tables get the correct single timeline with smart date logic
  // if (forecastData.merged && forecastData.merged.length > 0) {
  //   forecastData.merged.forEach((point) => {
  //     // Check if point has dataType field (MergedRebatePoint)
  //     const mergedPoint = point as MergedRebatePoint;
  //     if ('dataType' in mergedPoint && mergedPoint.dataType) {
  //       // Use dataType to determine isActual
  //       // past = black (isActual = true), future = blue (isActual = false)
  //       const isActual = mergedPoint.dataType === 'past';
  //       points.push({ ...point, isActual });
  //     } else {
  //       // Fallback to date-based logic for backward compatibility
  //       points.push({ ...point, isActual: isDateActual(point.date) });
  //     }
  //   });
  //   // Sort points by date to ensure chronological order
  //   return points.sort((a, b) => a.date.getTime() - b.date.getTime());
  // }

  // Fallback to original logic: combine estimates and predictions
  // This preserves backward compatibility and is used for charts that need separate lines
  // Also supports reported array when present (for breakdown table filtering)

  // Check if we're filtering for a specific line type by checking if estimates/predictions are empty
  // but reported has data (indicating filtered view)
  const hasReportedData =
    forecastData.reported && forecastData.reported.length > 0;
  const hasEstimateData =
    forecastData.estimates && forecastData.estimates.length > 0;
  const hasPredictionData =
    forecastData.predictions &&
    Object.keys(forecastData.predictions).length > 0;

  // If we have reported data but no estimates/predictions, we're filtering for "reported" line
  const isFilteredForReported =
    hasReportedData && !hasEstimateData && !hasPredictionData;

  // First, add all estimate points and determine isActual based on date
  if (hasEstimateData && !isFilteredForReported) {
    forecastData.estimates.forEach((point) => {
      const dateKey = point.date.toISOString();
      estimateDates.add(dateKey);
      points.push({ ...point, isActual: isDateActual(point.date) });
    });
  }

  // If filtering for reported, use reported points
  if (isFilteredForReported && hasReportedData) {
    forecastData.reported.forEach((point) => {
      const dateKey = point.date.toISOString();
      estimateDates.add(dateKey);
      points.push({ ...point, isActual: isDateActual(point.date) });
    });
  }

  // Then, add prediction points ONLY for dates where we don't have estimates/reported
  // Only add predictions if we're not filtering for a specific line type
  if (hasPredictionData && !isFilteredForReported) {
    Object.values(forecastData.predictions).forEach((methodPoints) => {
      methodPoints.forEach((point) => {
        const dateKey = point.date.toISOString();
        // Only add prediction if we don't already have data for this date
        if (!estimateDates.has(dateKey)) {
          points.push({ ...point, isActual: isDateActual(point.date) });
          // Track this date to avoid duplicate predictions
          estimateDates.add(dateKey);
        }
      });
    });
  }

  // Sort points by date to ensure chronological order
  return points.sort((a, b) => a.date.getTime() - b.date.getTime());
};

/**
 * Fuse prediction data with estimates for missing months
 * Predictions may only cover part of the year (e.g., April-Dec), so we fill in
 * missing months with estimates data (e.g., Jan-March from estimates)
 */
const fusePredictionsWithEstimates = (
  predictions: RebatePoint[] | undefined,
  estimates: RebatePoint[] | undefined,
): RebatePoint[] => {
  if (!predictions || predictions.length === 0) {
    return estimates || [];
  }
  if (!estimates || estimates.length === 0) {
    return predictions;
  }

  // Create a map of prediction dates (using year-month as key for monthly granularity)
  const predictionDateKeys = new Set<string>();
  predictions.forEach((point) => {
    const date = new Date(point.date);
    const key = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
    predictionDateKeys.add(key);
  });

  // Collect estimate points for months not covered by predictions
  const estimatesForMissingMonths: RebatePoint[] = [];
  estimates.forEach((point) => {
    const date = new Date(point.date);
    const key = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
    if (!predictionDateKeys.has(key)) {
      estimatesForMissingMonths.push(point);
    }
  });

  // Combine and sort by date
  const fused = [...estimatesForMissingMonths, ...predictions];
  return fused.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
};

/**
 * Get current year points based on primary line selection
 * Used by tables to determine which data source to display
 */
export const getCurrentYearPointsByPrimaryLine = (
  forecastData: UnifiedForecastResult,
  primaryLine: string,
): RebatePoint[] => {
  switch (primaryLine) {
    case 'estimates':
      return forecastData.estimates || [];
    case 'predictions':
      // Generic 'predictions' defaults to sarima (the new default algorithm)
      return (
        forecastData.predictions?.['sarima'] ||
        forecastData.predictions?.['linear-regression'] ||
        []
      );
    case 'reported':
      return forecastData.reported || [];
    case 'bestEstimate':
      // Best estimate uses sarima (the new default algorithm)
      return (
        forecastData.predictions?.['sarima'] ||
        forecastData.predictions?.['linear-regression'] ||
        []
      );
    case 'previousYear':
      // Fallback to estimates for current year if "Previous Year" somehow selected
      return forecastData.estimates || [];
    // Specific prediction algorithm selections - fuse with estimates for missing months
    case 'sarima':
      return fusePredictionsWithEstimates(
        forecastData.predictions?.['sarima'],
        forecastData.estimates,
      );
    case 'linear-regression':
      return fusePredictionsWithEstimates(
        forecastData.predictions?.['linear-regression'],
        forecastData.estimates,
      );
    case 'ai-forecasting':
      return fusePredictionsWithEstimates(
        forecastData.predictions?.['ai-forecasting'],
        forecastData.estimates,
      );
    case 'yoy-seasonal':
      return fusePredictionsWithEstimates(
        forecastData.predictions?.['yoy-seasonal'],
        forecastData.estimates,
      );
    default:
      // Default to sarima (the new default algorithm)
      return (
        forecastData.predictions?.['sarima'] ||
        forecastData.predictions?.['linear-regression'] ||
        []
      );
  }
};

/**
 * Get previous year points (ALWAYS Estimates, regardless of primary line selection)
 * Previous Year columns always show Estimates for YoY comparison
 */
export const getPreviousYearPoints = (
  previousYearData: UnifiedForecastResult | null,
): RebatePoint[] | null => {
  // Previous Year always shows Estimates, regardless of primary line selection
  return previousYearData?.estimates || null;
};

/**
 * Validates that cumulative data is monotonically increasing for actual (historical) data
 * This is critical for detecting backend data integrity issues
 */
export const validateCumulativeData = (
  points: (RebatePoint & { isActual: boolean })[],
  context = 'unknown',
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const actualPoints = points.filter((p) => p.isActual);

  for (let i = 1; i < actualPoints.length; i++) {
    const current = actualPoints[i];
    const previous = actualPoints[i - 1];

    // Check total rebate amount (with tolerance for floating-point rounding)
    if (current.totalRebateAmount < previous.totalRebateAmount - EPSILON) {
      const error = `Total rebate amount decreased from ${
        previous.totalRebateAmount
      } to ${
        current.totalRebateAmount
      } between ${previous.date.toISOString()} and ${current.date.toISOString()}`;
      errors.push(error);
    }

    // Check breakdown totals by supplier
    const prevSupplierTotals = new Map<string, number>();
    const currSupplierTotals = new Map<string, number>();

    previous.breakdowns?.forEach((b) => {
      const current = prevSupplierTotals.get(b.supplierId) || 0;
      prevSupplierTotals.set(b.supplierId, current + b.rebateAmount);
    });

    current.breakdowns?.forEach((b) => {
      const currentTotal = currSupplierTotals.get(b.supplierId) || 0;
      currSupplierTotals.set(b.supplierId, currentTotal + b.rebateAmount);
    });

    // Check each supplier's cumulative amount (with tolerance for floating-point rounding)
    for (const [supplierId, prevTotal] of prevSupplierTotals) {
      const currTotal = currSupplierTotals.get(supplierId) || 0;
      if (currTotal < prevTotal - EPSILON) {
        const error = `Supplier ${supplierId} rebate decreased from ${prevTotal} to ${currTotal} between ${previous.date.toISOString()} and ${current.date.toISOString()}`;
        errors.push(error);
      }
    }
  }

  const isValid = errors.length === 0;

  return { isValid, errors };
};

/**
 * Converts cumulative values to incremental (month-over-month) values
 * with detailed logging for debugging
 */
export const convertCumulativeToIncremental = (
  cumulativeData: Map<
    string,
    { value: number; isActual: boolean; year: number }
  >,
  headers: string[],
  entityId: string,
  entityType: 'supplier' | 'division',
  verbose = false,
): {
  incrementalData: Map<string, number>;
  totalIncremental: number;
  debugInfo: Array<{
    month: string;
    monthIndex: number;
    cumulative: number | null;
    previous: number;
    incremental: number | null;
    isActual: boolean | null;
    year: number | null;
    note?: string;
  }>;
} => {
  const incrementalData = new Map<string, number>();
  const debugInfo: Array<{
    month: string;
    monthIndex: number;
    cumulative: number | null;
    previous: number;
    incremental: number | null;
    isActual: boolean | null;
    year: number | null;
    note?: string;
  }> = [];
  let previousValue = 0;
  let totalIncremental = 0;

  headers.forEach((monthKey, index) => {
    const monthData = cumulativeData.get(monthKey);
    if (monthData) {
      const incrementalValue = monthData.value - previousValue;
      incrementalData.set(monthKey, incrementalValue);
      totalIncremental += incrementalValue;

      const debugEntry = {
        month: monthKey,
        monthIndex: index,
        cumulative: monthData.value,
        previous: previousValue,
        incremental: incrementalValue,
        isActual: monthData.isActual,
        year: monthData.year,
      };

      debugInfo.push(debugEntry);

      previousValue = monthData.value;
    } else {
      // Log missing data
      debugInfo.push({
        month: monthKey,
        monthIndex: index,
        cumulative: null,
        previous: previousValue,
        incremental: null,
        isActual: null,
        year: null,
        note: 'Missing data',
      });
    }
  });

  return { incrementalData, totalIncremental, debugInfo };
};

// BreakdownTable component types (imported from component)
export interface BreakdownGroup {
  id: string;
  label: string;
  rows: BreakdownDataRow[];
  values?: Record<
    string,
    number | { current: number; previous: number; delta: number } | null
  >;
  meta?: {
    rank?: number;
    totalValue?: number;
    year?: number;
    isProjected?: boolean;
  };
}

export interface BreakdownDataRow {
  id: string;
  label: string;
  values: Record<
    string,
    number | { current: number; previous: number; delta: number } | null
  >;
  meta?: {
    percentOfGroup?: string;
    isProjected?: boolean;
    rank?: number;
    totalValue?: number;
  };
}

export interface BreakdownData {
  headers: string[];
  groups: BreakdownGroup[];
}

// Month constants
export const ALL_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Month metadata including projected/actual status
 */
export interface MonthMetadata {
  month: string;
  isProjected: boolean;
  year: number;
}

/**
 * Enhanced breakdown data with month metadata
 */
export interface EnhancedBreakdownData extends BreakdownData {
  monthMetadata: MonthMetadata[];
}

/**
 * Creates month metadata from forecast data points
 * Supports multi-year: generates year-aware month keys (e.g., "Jan 2023") when multiple years present
 */
export const createMonthMetadata = (
  forecastData: UnifiedForecastResult,
): MonthMetadata[] => {
  const allPoints = getAllPoints(forecastData);
  if (!allPoints.length) {
    return [];
  }

  // Detect if we have multiple years in the data
  const yearsInData = new Set<number>();
  allPoints.forEach((point) => {
    yearsInData.add(point.date.getUTCFullYear());
  });
  const hasMultipleYears = yearsInData.size > 1;

  // Get date range from forecast period
  const startDate = forecastData.forecastPeriod?.startDate || allPoints[0].date;
  const endDate =
    forecastData.forecastPeriod?.endDate ||
    allPoints[allPoints.length - 1].date;

  const startYear = startDate.getUTCFullYear();
  const endYear = endDate.getUTCFullYear();

  // Build map of month+year -> metadata
  const monthYearMap = new Map<
    string,
    { isProjected: boolean; year: number }
  >();

  allPoints.forEach((point) => {
    const month = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      timeZone: 'UTC',
    }).format(point.date);
    const year = point.date.getUTCFullYear();
    const key = hasMultipleYears ? `${month} ${year}` : month;

    // Store the first occurrence of each month+year with its actual/projected status
    if (!monthYearMap.has(key)) {
      // Check if point has dataType field (MergedRebatePoint)
      let isProjected: boolean;
      if ('dataType' in point && (point as MergedRebatePoint).dataType) {
        // Use dataType to determine isProjected
        // past = black (not projected), future = blue (projected)
        const mergedPoint = point as MergedRebatePoint;
        isProjected = mergedPoint.dataType === 'future';
      } else {
        // Fallback to isActual for backward compatibility
        isProjected = !point.isActual;
      }
      monthYearMap.set(key, { isProjected, year });
    }
  });

  // Generate all months for all years in the range
  const metadata: MonthMetadata[] = [];

  for (let year = startYear; year <= endYear; year++) {
    ALL_MONTHS.forEach((monthName) => {
      // Determine if this month should be included based on date range
      const monthIndex = ALL_MONTHS.indexOf(monthName);
      const isFirstYear = year === startYear;
      const isLastYear = year === endYear;

      // Skip months before start date
      if (isFirstYear) {
        const startMonth = startDate.getUTCMonth();
        if (monthIndex < startMonth) return;
      }

      // Skip months after end date
      if (isLastYear) {
        const endMonth = endDate.getUTCMonth();
        if (monthIndex > endMonth) return;
      }

      const key = hasMultipleYears ? `${monthName} ${year}` : monthName;
      const data = monthYearMap.get(key);

      if (data) {
        metadata.push({
          month: key,
          isProjected: data.isProjected,
          year: data.year,
        });
      } else {
        // For months without data, default to projected status based on current date
        metadata.push({
          month: key,
          isProjected: isMonthProjected(monthName, year),
          year: year,
        });
      }
    });
  }

  return metadata;
};

/**
 * Determines if a month is projected (future) based on today's date
 * Now takes an optional year parameter for accurate comparison
 */
export const isMonthProjected = (monthName: string, year?: number): boolean => {
  const today = new Date();
  const currentMonth = today.getUTCMonth(); // 0-based (0 = January)
  const currentYear = today.getUTCFullYear();

  const monthIndex = ALL_MONTHS.indexOf(monthName);
  if (monthIndex === -1) return true; // Unknown month, assume projected

  const targetYear = year || currentYear;

  // If it's a future year, it's projected
  if (targetYear > currentYear) return true;

  // If it's a past year, it's actual
  if (targetYear < currentYear) return false;

  // Same year: compare months
  return monthIndex > currentMonth;
};

/**
 * Creates supplier ID to name mapping from forecast data
 */
export const createSupplierIdToNameMap = (
  forecastData: UnifiedForecastResult,
): Map<string, string> => {
  const supplierMap = new Map<string, string>();
  const allPoints = getAllPoints(forecastData);

  allPoints.forEach((point) => {
    point.breakdowns?.forEach((breakdown: RebateBreakdown) => {
      // Use supplierName only if present; otherwise leave mapping for external lookup
      if (breakdown.supplierName) {
        supplierMap.set(breakdown.supplierId, breakdown.supplierName);
      } else if (!supplierMap.has(breakdown.supplierId)) {
        // Ensure the ID exists as a key to be resolved by by-ids fetch
        supplierMap.set(breakdown.supplierId, breakdown.supplierId);
      }
    });
  });

  return supplierMap;
};

/**
 * Creates division ID to name mapping from forecast data
 */
export const createDivisionIdToNameMap = (
  forecastData: UnifiedForecastResult,
): Map<string, string> => {
  const divisionMap = new Map<string, string>();
  const allPoints = getAllPoints(forecastData);

  allPoints.forEach((point) => {
    point.breakdowns?.forEach((breakdown: RebateBreakdown) => {
      divisionMap.set(breakdown.divisionId, breakdown.divisionName);
    });
  });

  return divisionMap;
};

/**
 * Creates rule ID to name mapping from forecast data
 */
export const createRuleIdToNameMap = (
  forecastData: UnifiedForecastResult,
): Map<string, string> => {
  const ruleMap = new Map<string, string>();
  const allPoints = getAllPoints(forecastData);

  allPoints.forEach((point) => {
    point.breakdowns?.forEach((breakdown: RebateBreakdown) => {
      ruleMap.set(breakdown.ruleId, breakdown.ruleName);
    });
  });

  return ruleMap;
};

/**
 * Generic breakdown configuration for the transform functions
 */
interface BreakdownConfig {
  primaryKey: 'supplierId' | 'ruleId' | 'divisionId';
  secondaryKey: 'divisionId' | 'supplierId' | 'ruleId';
  primaryNameKey: 'supplierName' | 'ruleName' | 'divisionName';
  secondaryNameKey: 'divisionName' | 'supplierName' | 'ruleName';
  contextName: string;
  primaryNameMap: Map<string, string>;
  secondaryNameMap: Map<string, string>;
  useCumulative?: boolean; // If true, keep cumulative values; if false, convert to incremental
}

/**
 * Generic function to transform forecast data into hierarchical breakdown
 *
 * This eliminates duplication between supplier, rule, and division breakdowns
 * while maintaining the same robust cumulative→incremental conversion logic.
 */
const transformToGenericBreakdown = (
  forecastData: UnifiedForecastResult,
  config: BreakdownConfig,
): EnhancedBreakdownData => {
  const allPoints = getAllPoints(forecastData);
  if (!allPoints.length) {
    return {
      headers: [],
      groups: [],
      monthMetadata: [],
    };
  }

  // Create month metadata first
  const monthMetadata = createMonthMetadata(forecastData);
  const headers = monthMetadata.map((m) => m.month);

  // Use all points with data (both actual and forecast), excluding empty first point
  const validPoints = allPoints.filter(
    (point) => point.totalRebateAmount > 0 && point.breakdowns.length > 0,
  );

  if (validPoints.length === 0) {
    return {
      headers,
      groups: [],
      monthMetadata,
    };
  }

  // Detect if we have multiple years (same logic as createMonthMetadata)
  const yearsInData = new Set<number>();
  validPoints.forEach((point) => {
    yearsInData.add(point.date.getUTCFullYear());
  });
  const hasMultipleYears = yearsInData.size > 1;

  /*
   * FIRST PASS: Aggregate flat breakdowns by primary/secondary/month
   * Store cumulative values from the API response
   */
  const primaryMonthlyData = new Map<
    string,
    Map<string, { value: number; isActual: boolean; year: number }>
  >();
  const secondaryMonthlyData = new Map<
    string,
    Map<string, Map<string, { value: number; isActual: boolean; year: number }>>
  >();

  // Store names for later use (for rules, we need to extract from breakdown data)
  const primaryIdToName = new Map<string, string>();

  validPoints.forEach((point) => {
    const month = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      timeZone: 'UTC',
    }).format(point.date);
    const year = point.date.getUTCFullYear();
    // Use year-aware month key if multiple years present
    const monthKey = hasMultipleYears ? `${month} ${year}` : month;

    // Group breakdowns by primary and secondary keys
    const primaryTotals = new Map<string, number>();
    const primarySecondaryTotals = new Map<string, Map<string, number>>();

    point.breakdowns.forEach((breakdown: RebateBreakdown) => {
      // Get IDs from breakdown
      const primaryId = breakdown[config.primaryKey];
      const secondaryId = breakdown[config.secondaryKey];

      // Store names (especially important for rules which aren't in separate API)
      primaryIdToName.set(primaryId, breakdown[config.primaryNameKey]);

      // Aggregate primary totals
      const currentPrimaryTotal = primaryTotals.get(primaryId) || 0;
      primaryTotals.set(
        primaryId,
        currentPrimaryTotal + breakdown.rebateAmount,
      );

      // Aggregate secondary totals for each primary
      if (!primarySecondaryTotals.has(primaryId)) {
        primarySecondaryTotals.set(primaryId, new Map());
      }
      const secondaryTotals = primarySecondaryTotals.get(primaryId)!;
      const currentSecondaryTotal = secondaryTotals.get(secondaryId) || 0;
      secondaryTotals.set(
        secondaryId,
        currentSecondaryTotal + breakdown.rebateAmount,
      );
    });

    // Store primary monthly totals
    primaryTotals.forEach((total, primaryId) => {
      if (!primaryMonthlyData.has(primaryId)) {
        primaryMonthlyData.set(primaryId, new Map());
      }
      primaryMonthlyData.get(primaryId)!.set(monthKey, {
        value: total,
        isActual: point.isActual,
        year: year,
      });
    });

    // Store secondary monthly totals for each primary
    primarySecondaryTotals.forEach((secondaryTotals, primaryId) => {
      if (!secondaryMonthlyData.has(primaryId)) {
        secondaryMonthlyData.set(primaryId, new Map());
      }

      secondaryTotals.forEach((total, secondaryId) => {
        if (!secondaryMonthlyData.get(primaryId)!.has(secondaryId)) {
          secondaryMonthlyData.get(primaryId)!.set(secondaryId, new Map());
        }
        secondaryMonthlyData.get(primaryId)!.get(secondaryId)!.set(monthKey, {
          value: total,
          isActual: point.isActual,
          year: year,
        });
      });
    });
  });

  /*
   * SECOND PASS: Convert cumulative to incremental values OR keep cumulative
   * Based on useCumulative flag
   */
  const useCumulative = config.useCumulative ?? false;
  const primaryTotals = new Map<string, number>();
  const primaryMonthlyDataProcessed = new Map<string, Map<string, number>>();
  const secondaryMonthlyDataProcessed = new Map<
    string,
    Map<string, Map<string, number>>
  >();

  for (const [primaryId, monthlyData] of primaryMonthlyData) {
    primaryMonthlyDataProcessed.set(primaryId, new Map());
    secondaryMonthlyDataProcessed.set(primaryId, new Map());

    const primaryName =
      config.primaryNameMap.get(primaryId) ||
      primaryIdToName.get(primaryId) ||
      primaryId;

    if (useCumulative) {
      // Keep cumulative values as-is
      const cumulativeMap = new Map<string, number>();
      let lastCumulativeValue = 0;
      headers.forEach((month) => {
        const monthData = monthlyData.get(month);
        if (monthData) {
          cumulativeMap.set(month, monthData.value);
          lastCumulativeValue = monthData.value;
        } else {
          cumulativeMap.set(month, lastCumulativeValue);
        }
      });
      primaryMonthlyDataProcessed.set(primaryId, cumulativeMap);
      primaryTotals.set(primaryId, lastCumulativeValue);
    } else {
      // Convert cumulative to incremental
      const primaryConversion = convertCumulativeToIncremental(
        monthlyData,
        headers,
        primaryName,
        'supplier', // using 'supplier' type for consistency
        false, // quiet mode
      );
      primaryMonthlyDataProcessed.set(
        primaryId,
        primaryConversion.incrementalData,
      );
      primaryTotals.set(primaryId, primaryConversion.totalIncremental);
    }

    // Process secondary data for this primary
    const primarySecondaries = secondaryMonthlyData.get(primaryId) || new Map();
    for (const [secondaryId, secondaryMonthly] of primarySecondaries) {
      const secondaryName =
        config.secondaryNameMap.get(secondaryId) || secondaryId;

      if (useCumulative) {
        // Keep cumulative values as-is
        const cumulativeMap = new Map<string, number>();
        let lastCumulativeValue = 0;
        headers.forEach((month) => {
          const monthData = secondaryMonthly.get(month);
          if (monthData) {
            cumulativeMap.set(month, monthData.value);
            lastCumulativeValue = monthData.value;
          } else {
            cumulativeMap.set(month, lastCumulativeValue);
          }
        });
        if (!secondaryMonthlyDataProcessed.get(primaryId)) {
          secondaryMonthlyDataProcessed.set(primaryId, new Map());
        }
        secondaryMonthlyDataProcessed
          .get(primaryId)!
          .set(secondaryId, cumulativeMap);
      } else {
        // Convert cumulative to incremental
        const secondaryConversion = convertCumulativeToIncremental(
          secondaryMonthly,
          headers,
          `${primaryName}->${secondaryName}`,
          'division',
          false, // quiet mode
        );
        if (!secondaryMonthlyDataProcessed.get(primaryId)) {
          secondaryMonthlyDataProcessed.set(primaryId, new Map());
        }
        secondaryMonthlyDataProcessed
          .get(primaryId)!
          .set(secondaryId, secondaryConversion.incrementalData);
      }
    }
  }

  // Build groups array (primary entities)
  const groups = Array.from(primaryTotals.entries())
    .map(([primaryId, total], index) => {
      const primaryName =
        config.primaryNameMap.get(primaryId) ||
        primaryIdToName.get(primaryId) ||
        primaryId;

      // Build monthly data for primary
      const primaryMonthly =
        primaryMonthlyDataProcessed.get(primaryId) || new Map();
      const values: Record<string, number | null> = {};
      headers.forEach((month) => {
        const value = primaryMonthly.get(month);
        values[month] = value !== undefined ? value : null;
      });

      // Build sub-rows (secondary entities)
      const rows = Array.from(
        secondaryMonthlyDataProcessed.get(primaryId) || new Map(),
      )
        .map(([secondaryId, secondaryMonthly]) => {
          const secondaryName =
            config.secondaryNameMap.get(secondaryId) || secondaryId;

          // Build monthly data for secondary
          const secondaryValues: Record<string, number | null> = {};
          headers.forEach((month) => {
            const value = secondaryMonthly.get(month);
            secondaryValues[month] = value !== undefined ? value : null;
          });

          // Calculate percentage based on data type
          let secondaryMonthlyTotal: number;
          let primaryMonthlyTotal: number;

          if (useCumulative) {
            // For cumulative: use the last non-null value
            const secondaryValuesArray = Object.values(secondaryValues).filter(
              (v): v is number => v !== null,
            );
            const primaryValuesArray = Object.values(values).filter(
              (v): v is number => v !== null,
            );
            secondaryMonthlyTotal =
              secondaryValuesArray.length > 0
                ? secondaryValuesArray[secondaryValuesArray.length - 1]
                : 0;
            primaryMonthlyTotal =
              primaryValuesArray.length > 0
                ? primaryValuesArray[primaryValuesArray.length - 1]
                : 0;
          } else {
            // For incremental: sum all monthly values
            secondaryMonthlyTotal = Object.values(secondaryValues).reduce(
              (sum: number, val) => sum + (val || 0),
              0,
            );
            primaryMonthlyTotal = Object.values(values).reduce(
              (sum: number, val) => sum + (val || 0),
              0,
            );
          }

          return {
            id: secondaryId,
            label: secondaryName,
            values: secondaryValues,
            meta: {
              percentOfGroup:
                primaryMonthlyTotal > 0
                  ? `${(
                      (secondaryMonthlyTotal / primaryMonthlyTotal) *
                      100
                    ).toFixed(1)}% of ${primaryName}`
                  : '0.0%',
              isProjected: false,
            },
          };
        })
        .sort((a, b) => {
          const aTotal = Object.values(a.values).reduce(
            (sum: number, val) => sum + (val || 0),
            0,
          );
          const bTotal = Object.values(b.values).reduce(
            (sum: number, val) => sum + (val || 0),
            0,
          );
          return bTotal - aTotal;
        });

      return {
        id: primaryId,
        label: primaryName,
        rows,
        values,
        meta: {
          rank: index + 1,
          totalValue: total,
        },
      };
    })
    .sort((a, b) => (b.meta?.totalValue || 0) - (a.meta?.totalValue || 0));

  return {
    headers,
    groups,
    monthMetadata,
  };
};

export interface TransformToRuleBreakdownProps {
  forecastData: UnifiedForecastResult;
  divisionIdToName: Map<string, string>;
  useCumulative?: boolean;
}
export const transformToRuleBreakdown = ({
  forecastData,
  divisionIdToName,
  useCumulative,
}: TransformToRuleBreakdownProps): EnhancedBreakdownData => {
  // For rules, we don't have a separate name map since names come from breakdown data
  const emptyRuleNameMap = new Map<string, string>();

  return transformToGenericBreakdown(forecastData, {
    primaryKey: 'ruleId',
    secondaryKey: 'divisionId',
    primaryNameKey: 'ruleName',
    secondaryNameKey: 'divisionName',
    contextName: 'RuleBreakdown',
    primaryNameMap: emptyRuleNameMap, // Will use names from breakdown data
    secondaryNameMap: divisionIdToName,
    useCumulative,
  });
};

export interface TransformToDivisionRuleBreakdownProps {
  forecastData: UnifiedForecastResult;
  ruleIdToName: Map<string, string>;
  divisionIdToName: Map<string, string>;
  useCumulative?: boolean;
}
export const transformToDivisionRuleBreakdown = ({
  forecastData,
  ruleIdToName,
  divisionIdToName,
  useCumulative,
}: TransformToDivisionRuleBreakdownProps): EnhancedBreakdownData => {
  return transformToGenericBreakdown(forecastData, {
    primaryKey: 'divisionId',
    secondaryKey: 'ruleId',
    primaryNameKey: 'divisionName',
    secondaryNameKey: 'ruleName',
    contextName: 'DivisionRuleBreakdown',
    primaryNameMap: divisionIdToName,
    secondaryNameMap: ruleIdToName,
    useCumulative,
  });
};

/**
 * Helper function to find if a month is projected based on metadata
 */
export const getUTCMonthProjectedStatus = (
  monthMetadata: { month: string; isProjected: boolean }[],
  monthHeader: string,
): boolean => {
  const monthInfo = monthMetadata?.find((m) => m.month === monthHeader);
  return monthInfo?.isProjected ?? false;
};

/**
 * Normalize daysFromStart values to account for leap year differences.
 * When comparing years where one is a leap year and the other is not,
 * dates after February will be off by 1 day.
 *
 * @param forecastData - The forecast data to normalize
 * @param offset - The offset to apply to daysFromStart values after Feb 28
 *                 (-1 if previous year was leap year, +1 if current year is leap year)
 * @returns Normalized forecast data with adjusted daysFromStart values
 */
export const normalizeDaysFromStartForLeapYear = (
  forecastData: UnifiedForecastResult,
  offset: number,
): UnifiedForecastResult => {
  if (offset === 0) return forecastData;

  // Helper to adjust a single point's daysFromStart if after Feb 28
  const adjustPoint = (point: RebatePoint): RebatePoint => {
    const month = point.date.getUTCMonth(); // 0-based: 0=Jan, 1=Feb
    const day = point.date.getUTCDate();

    // Only adjust dates after Feb 28 (month > 1, or month === 1 and day > 28)
    const isAfterFeb28 = month > 1 || (month === 1 && day > 28);

    if (isAfterFeb28) {
      return {
        ...point,
        daysFromStart: point.daysFromStart + offset,
      };
    }

    return point;
  };

  // Adjust estimates
  const normalizedEstimates = forecastData.estimates?.map(adjustPoint);

  // Adjust predictions
  const normalizedPredictions = forecastData.predictions
    ? Object.fromEntries(
        Object.entries(forecastData.predictions).map(([method, points]) => [
          method,
          points.map(adjustPoint),
        ]),
      )
    : {};

  return {
    ...forecastData,
    estimates: normalizedEstimates,
    predictions: normalizedPredictions,
  };
};

/**
 * DEBUG HELPER: Run basic integrity checks on forecast data
 * Call this from browser console: window.debugForecastData(forecastData)
 */
export const debugForecastData = (forecastData: UnifiedForecastResult) => {
  console.log('🔍 DEBUGGING FORECAST DATA');

  const allPoints = getAllPoints(forecastData);
  console.log(`Total points: ${allPoints.length}`);

  const actualPoints = allPoints.filter((p) => p.isActual);
  const projectedPoints = allPoints.filter((p) => !p.isActual);

  console.log(`Actual points: ${actualPoints.length}`);
  console.log(`Projected points: ${projectedPoints.length}`);

  // Check for negative deltas in total rebate amounts
  console.log('\n📊 TOTAL REBATE AMOUNT PROGRESSION:');
  for (let i = 0; i < actualPoints.length; i++) {
    const point = actualPoints[i];
    const prevPoint = i > 0 ? actualPoints[i - 1] : null;
    const delta = prevPoint
      ? point.totalRebateAmount - prevPoint.totalRebateAmount
      : point.totalRebateAmount;

    console.log(
      `${point.date.toISOString().slice(0, 10)}: ${
        point.totalRebateAmount
      } (Δ ${delta})`,
    );

    if (delta < 0 && prevPoint) {
      console.warn(
        `❌ NEGATIVE DELTA detected between ${prevPoint.date
          .toISOString()
          .slice(0, 10)} and ${point.date.toISOString().slice(0, 10)}`,
      );
    }
  }

  // Run validation
  const validation = validateCumulativeData(allPoints, 'Debug');

  return {
    totalPoints: allPoints.length,
    actualPoints: actualPoints.length,
    projectedPoints: projectedPoints.length,
    validation,
    points: allPoints,
  };
};

// Make debug helper available globally in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (
    window as { debugForecastData?: typeof debugForecastData }
  ).debugForecastData = debugForecastData;
}
